import { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  Eye,
  EyeOff,
  User as UserIcon,
  Loader2,
  Shield,
  Lock,
  DollarSign,
  Target,
  TrendingUp,
  Percent,
  PieChart,
  Briefcase,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import "flag-icons/css/flag-icons.min.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { countries, countryCodeMap } from "@/data/countries";

export interface Role {
  id: string;
  name: string;
  is_system_role?: boolean;
  pipeline_permissions?: {
    pipeline: {
      id: string;
      name: string;
      income_objective: number;
    };
  }[];
}

type TargetType = "fixed" | "percentage";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    password?: string;
    roleId: string;
    is_active?: boolean;
    sales_target_value?: number;
    target_type?: TargetType;
    pipeline_id?: string | null;
  }) => Promise<void>;
  user?: any | null;
  roles: Role[];
  currentUserIsOwner: boolean;
  existingUsers?: any[];
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export function UserModal({
  isOpen,
  onClose,
  onSave,
  user,
  roles,
  currentUserIsOwner,
  existingUsers = [],
}: UserModalProps) {
  const [activeTab, setActiveTab] = useState<"personal" | "professional">(
    "personal",
  );

  const { toast } = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [roleId, setRoleId] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [countryCode, setCountryCode] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // ✅ Campos de meta del usuario
  const [salesTargetValue, setSalesTargetValue] = useState("");
  const [targetType, setTargetType] = useState<TargetType>("fixed");

  // ✅ Estado para el pipeline seleccionado
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(
    null,
  );

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rol seleccionado
  const selectedRole = roles.find((r) => r.id === roleId) || null;

  // ✅ Pipelines disponibles para el rol seleccionado
  const availablePipelines = useMemo(() => {
    if (!selectedRole || !selectedRole.pipeline_permissions) return [];
    return selectedRole.pipeline_permissions
      .map((p) => p.pipeline)
      .filter(Boolean);
  }, [selectedRole]);

  // ✅ Pipeline seleccionado
  const selectedPipeline = useMemo(() => {
    if (!selectedPipelineId) return null;
    return availablePipelines.find((p) => p.id === selectedPipelineId);
  }, [selectedPipelineId, availablePipelines]);

  // ✅ Calcular distribución de la meta
  const {
    allocatedAmount,
    totalPipelineGoal,
    remainingAmount,
    currentAllocatedPercent,
  } = useMemo(() => {
    if (!selectedPipeline)
      return {
        allocatedAmount: 0,
        totalPipelineGoal: 0,
        remainingAmount: 0,
        currentAllocatedPercent: 0,
      };

    const goal = selectedPipeline.income_objective || 0;

    // Sumar metas de OTROS usuarios que tienen este rol (asumiendo que contribuyen a este pipeline)
    // Nota: Como la meta es por usuario, hacemos una estimación basada en el rol.
    const otherUsersTotal = existingUsers
      .filter(
        (u) =>
          (u.role?.id === roleId || u.id_rol === roleId) && // Mismo rol
          u.id !== user?.id, // Excluir usuario actual si es edición
      )
      .reduce((sum, u) => sum + parseFloat(u.sales_target || 0), 0);

    return {
      allocatedAmount: otherUsersTotal,
      totalPipelineGoal: goal,
      remainingAmount: Math.max(0, goal - otherUsersTotal),
      currentAllocatedPercent: goal > 0 ? (otherUsersTotal / goal) * 100 : 0,
    };
  }, [selectedPipeline, existingUsers, roleId, user]);

  useEffect(() => {
    if (isOpen) {
      console.log("🟢 [UserModal] Modal Abierto. User prop:", user);
      if (user) {
        console.log("🟢 [UserModal] Modo Edición. Cargando datos...", user);
        setFirstName(user.first_name || "");
        setLastName(user.last_name || "");
        setEmail(user.email || "");

        let cleanPhone = user.phone || "";
        let code = "";
        if (cleanPhone.startsWith("+")) {
          const matchedCountry = countries.find((c) =>
            cleanPhone.startsWith(c.code),
          );
          if (matchedCountry) {
            code = matchedCountry.code;
            cleanPhone = cleanPhone.substring(code.length).trim();
          }
        }
        setPhone(cleanPhone);
        setCountryCode(code);
        const country = countries.find((c) => c.code === code);
        setSearchTerm(
          country ? `${country.name} (${country.code})` : code || "",
        );
        setIsActive(
          user.is_active ??
            user.isActive ??
            (user.estado !== 0 && user.estado !== false),
        );
        setRoleId(user.id_rol || user.role?.id || user.roleId || "");

        // FIX: Se leen las propiedades de la meta con múltiples nombres posibles (snake_case vs camelCase)
        // para asegurar que se encuentren sin importar cómo lleguen desde el API.
        const targetTypeVal = user.target_type || user.targetType || "fixed";
        const salesTargetVal = user.sales_target ?? user.salesTarget;
        const calcPercentVal =
          user.calculated_percentage ?? user.calculatedPercentage;

        // Log para depurar qué valores se están encontrando.
        console.log("🎯 [UserModal] Configurando meta:", {
          target_type: targetTypeVal, // Debería ser 'fixed' o 'percentage'
          calculated_percentage: calcPercentVal,
          sales_target: salesTargetVal, // Debería tener el valor de la DB
        });

        // FIX: Lógica mejorada para establecer el valor del input.
        // Si el tipo es 'percentage', usamos el porcentaje calculado.
        // Si no, usamos el valor de la meta directamente.
        if (
          targetTypeVal === "percentage" &&
          calcPercentVal !== undefined &&
          calcPercentVal !== null
        ) {
          setSalesTargetValue(calcPercentVal.toString());
        } else if (salesTargetVal !== undefined && salesTargetVal !== null) {
          setSalesTargetValue(salesTargetVal.toString());
        } else {
          setSalesTargetValue("");
        }
        setTargetType(targetTypeVal as TargetType); // Se asegura que el tipo de meta se establezca.
        setPassword("");
        setConfirmPassword("");
      } else {
        console.log("🟢 [UserModal] Modo Creación. Reseteando formulario.");
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setCountryCode("");
        setSearchTerm("");
        setIsActive(true);
        setRoleId("");
        setSalesTargetValue("");
        setTargetType("fixed");
        setPassword("");
        setConfirmPassword("");
      }

      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [user, isOpen]);

  // Auto-seleccionar pipeline si solo hay uno o si el rol cambia
  useEffect(() => {
    console.log(
      "🔄 [UserModal] Evaluando Pipelines disponibles:",
      availablePipelines,
    );
    if (availablePipelines.length === 1) {
      console.log(
        "⚡ [UserModal] Auto-seleccionando pipeline único:",
        availablePipelines[0].name,
      );
      setSelectedPipelineId(availablePipelines[0].id);
    } else if (user && isOpen && availablePipelines.length > 0) {
      // Si estamos editando, intentamos encontrar el pipeline que coincida con la meta base
      // Solo si el rol seleccionado sigue siendo el original del usuario
      const currentRoleId = user.id_rol || user.role?.id || user.roleId;
      const objective = user.pipeline_objective ?? user.pipelineObjective;

      if (currentRoleId === roleId && objective) {
        console.log(
          "🔄 [UserModal] Buscando match de pipeline por objetivo:",
          objective,
        );
        const match = availablePipelines.find(
          (p) => p.income_objective === objective,
        );
        if (match) {
          console.log("✅ [UserModal] Pipeline encontrado:", match.name);
          setSelectedPipelineId(match.id);
        } else {
          console.warn(
            "⚠️ [UserModal] No se encontró pipeline que coincida con el objetivo guardado.",
          );
          // Fallback: Si el usuario tiene meta pero no encontramos match exacto, seleccionamos el primero
          // para que el UI no salga vacío.
          if (salesTargetValue && !selectedPipelineId) {
            setSelectedPipelineId(availablePipelines[0].id);
          }
        }
      } else if (salesTargetValue && !selectedPipelineId) {
        // Fallback general si hay meta definida pero no pipeline asociado explícitamente
        setSelectedPipelineId(availablePipelines[0].id);
      }
    }
  }, [availablePipelines, user, isOpen, roleId, salesTargetValue]);

  const availableRoles = roles.filter((role) => {
    if (currentUserIsOwner) return true;
    if (role.is_system_role) return false;
    return true;
  });

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = "El nombre es obligatorio";
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = "El nombre debe tener al menos 2 caracteres";
    }

    if (!lastName.trim()) {
      newErrors.lastName = "El apellido es obligatorio";
    } else if (lastName.trim().length < 2) {
      newErrors.lastName = "El apellido debe tener al menos 2 caracteres";
    }

    if (!email.trim()) {
      newErrors.email = "El email es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Formato de email inválido";
    }

    if (phone.trim() && countryCode) {
      const country = countries.find((c) => c.code === countryCode);
      if (country) {
        const libPhoneCountry = countryCodeMap[country.flagCode];
        const phoneNumber = parsePhoneNumberFromString(
          phone,
          libPhoneCountry as any,
        );
        if (!phoneNumber || !phoneNumber.isValid()) {
          newErrors.phone = "Número inválido para el país seleccionado";
        }
      }
    } else if (phone.trim() && !countryCode) {
      newErrors.countryCode = "Seleccione un país";
    }

    if (!user) {
      if (!password) {
        newErrors.password = "La contraseña es obligatoria";
      } else if (password.length < 8) {
        newErrors.password = "La contraseña debe tener al menos 8 caracteres";
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
        newErrors.password = "Debe contener mayúsculas, minúsculas y números";
      }

      if (!confirmPassword) {
        newErrors.confirmPassword = "Confirma la contraseña";
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = "Las contraseñas no coinciden";
      }
    }

    if (!roleId) {
      newErrors.roleId = "Selecciona un rol";
    }

    if (salesTargetValue.trim()) {
      const numVal = parseFloat(salesTargetValue);
      if (isNaN(numVal) || numVal < 0) {
        newErrors.salesTargetValue = "Ingresa un valor válido mayor a 0";
      } else if (targetType === "percentage" && numVal > 100) {
        newErrors.salesTargetValue = "El porcentaje no puede superar 100%";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("🔵 [UserModal] handleSubmit iniciado");
    if (!validateForm()) {
      console.warn("🔴 [UserModal] Validación fallida:", errors);
      toast({
        title: "Error de validación",
        description: "Por favor corrige los errores en el formulario",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const selectedCountryData = countries.find((c) => c.code === countryCode);
      const formattedPhone = selectedCountryData
        ? `${selectedCountryData.code} ${phone.trim()}`
        : phone.trim();

      const userData: any = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),

        email: email.trim(),
        roleId,
        is_active: isActive,
        phone: formattedPhone,
        // ✅ Se envían solo si el usuario ingresó un valor
        ...(salesTargetValue
          ? {
              sales_target_value: parseFloat(salesTargetValue),
              target_type: targetType,
              pipeline_id: selectedPipelineId,
            }
          : {}),
        ...(!user && { password }),
      };

      console.log(
        "🚀 [UserModal] Enviando a onSave:",
        JSON.stringify(userData, null, 2),
      );
      await onSave(userData);
      console.log("✅ [UserModal] Guardado exitoso");
      onClose();
    } catch (error: any) {
      console.error("❌ [UserModal] Error en onSave:", error);
      toast({
        title: "Error",
        description: error.message || "Hubo un problema al guardar el usuario",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    switch (field) {
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "phone":
        setPhone(value.replace(/\D/g, ""));
        break;
      case "password":
        setPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
      case "salesTargetValue":
        if (/^\d*\.?\d*$/.test(value)) setSalesTargetValue(value);
        break;
    }
  };

  const handleCountryInputChange = (value: string) => {
    setSearchTerm(value);
    setShowDropdown(true);
  };

  const filteredCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code.includes(searchTerm),
  );

  const handleSelectCountry = (country: (typeof countries)[0]) => {
    setCountryCode(country.code);
    setSearchTerm(`${country.name} (${country.code})`);
    setShowDropdown(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        <DialogHeader className="p-6 pb-2 border-b bg-white z-10">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <UserIcon className="h-5 w-5" />
            {user ? "Editar Usuario" : "Nuevo Usuario"}
          </DialogTitle>
          <DialogDescription>
            {user
              ? "Modifica la información del usuario existente."
              : "Ingresa los datos para registrar un nuevo usuario."}
          </DialogDescription>

          {/* Tabs Navigation */}
          <div className="flex items-center gap-6 mt-6">
            <button
              onClick={() => setActiveTab("personal")}
              className={`pb-3 text-sm font-medium transition-all relative px-1 ${
                activeTab === "personal"
                  ? "text-blue-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <UserIcon className="h-4 w-4" />
                Datos Personales
              </div>
            </button>
            <button
              onClick={() => setActiveTab("professional")}
              className={`pb-3 text-sm font-medium transition-all relative px-1 ${
                activeTab === "professional"
                  ? "text-blue-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Rol y Metas
              </div>
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeTab === "personal" && (
            <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Nombre *</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) =>
                      handleFieldChange("firstName", e.target.value)
                    }
                    placeholder="Ej: María"
                    disabled={isSubmitting}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs flex items-center mt-1">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Apellido *</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) =>
                      handleFieldChange("lastName", e.target.value)
                    }
                    placeholder="Ej: González"
                    disabled={isSubmitting}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs flex items-center mt-1">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  placeholder="ejemplo@empresa.com"
                  disabled={isSubmitting || !!user}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs flex items-center mt-1">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.email}
                  </p>
                )}
                {user && (
                  <p className="text-xs text-slate-500">
                    El email no se puede modificar
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <div className="flex gap-2 relative">
                  <div className="relative w-32 md:w-40">
                    <Input
                      ref={inputRef}
                      value={searchTerm}
                      onChange={(e) => handleCountryInputChange(e.target.value)}
                      placeholder="País"
                      className="bg-white"
                    />
                    {showDropdown && (
                      <div className="absolute z-50 bg-white border mt-1 max-h-60 overflow-y-auto w-full shadow-lg rounded">
                        {filteredCountries.map((c) => (
                          <div
                            key={c.code}
                            className="cursor-pointer px-2 py-1 hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => handleSelectCountry(c)}
                          >
                            <span className={`fi fi-${c.flagCode} w-6 h-4`} />
                            <span className="text-sm truncate">{c.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => handleFieldChange("phone", e.target.value)}
                    placeholder="Número de teléfono"
                    className="flex-1"
                    disabled={isSubmitting}
                  />
                </div>
                {(errors.phone || errors.countryCode) && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.phone || errors.countryCode}
                  </p>
                )}
              </div>

              {!user && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) =>
                          handleFieldChange("password", e.target.value)
                        }
                        placeholder="Mínimo 8 caracteres"
                        disabled={isSubmitting}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        disabled={isSubmitting}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar *</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) =>
                          handleFieldChange("confirmPassword", e.target.value)
                        }
                        placeholder="Repite la contraseña"
                        disabled={isSubmitting}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        disabled={isSubmitting}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "professional" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="space-y-2">
                <Label htmlFor="role" className="flex items-center gap-1">
                  <Shield className="h-4 w-4" />
                  Rol *
                </Label>
                <Select
                  value={roleId}
                  onValueChange={(value) => {
                    handleFieldChange("roleId", value);
                    setRoleId(value);
                  }}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.length > 0 ? (
                      availableRoles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                          {role.is_system_role && " (Sistema)"}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>
                        No hay roles disponibles
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Sección de Metas */}
              {roleId && availablePipelines.length > 0 && (
                <div className="space-y-4 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Configuración de Metas
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Pipeline</Label>
                      <Select
                        value={selectedPipelineId || ""}
                        onValueChange={setSelectedPipelineId}
                        disabled={availablePipelines.length <= 1}
                      >
                        <SelectTrigger className="bg-slate-50">
                          <SelectValue placeholder="Seleccionar pipeline..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePipelines.map((pipeline) => (
                            <SelectItem key={pipeline.id} value={pipeline.id}>
                              {pipeline.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedPipeline && (
                      <div className="space-y-2">
                        <Label className="text-xs">Objetivo Global</Label>
                        <div className="px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-md flex items-center justify-between">
                          <span className="text-xs text-emerald-700 font-medium">
                            Meta del Equipo
                          </span>
                          <div className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3 text-emerald-600" />
                            <span className="text-sm font-bold text-emerald-700">
                              {formatCurrency(
                                selectedPipeline.income_objective,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Tipo de Meta</Label>
                      <Select
                        value={targetType}
                        onValueChange={(value) => {
                          setTargetType(value as TargetType);
                          if (errors.salesTargetValue) {
                            setErrors((prev) => ({
                              ...prev,
                              salesTargetValue: "",
                            }));
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fijo ($)</SelectItem>
                          <SelectItem value="percentage">
                            Porcentaje (%)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Valor Meta</Label>
                      <div className="relative">
                        {selectedPipeline ? (
                          targetType === "fixed" ? (
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          ) : (
                            <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          )
                        ) : (
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        )}
                        <Input
                          id="salesTargetValue"
                          value={salesTargetValue}
                          onChange={(e) =>
                            handleFieldChange(
                              "salesTargetValue",
                              e.target.value,
                            )
                          }
                          placeholder={targetType === "fixed" ? "0.00" : "0"}
                          className="pl-9"
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.salesTargetValue && (
                        <p className="text-red-500 text-[10px] mt-1">
                          {errors.salesTargetValue}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ✅ Barra de Distribución de Meta */}
                  {selectedPipeline && (
                    <div className="mt-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex justify-between items-center mb-2 text-xs">
                        <span className="font-medium text-slate-600 flex items-center gap-1">
                          <PieChart className="h-3 w-3" />
                          Distribución
                        </span>
                        <span className="text-slate-500">
                          {formatCurrency(totalPipelineGoal)}
                        </span>
                      </div>

                      {/* Barra de progreso visual */}
                      <div className="h-2.5 w-full bg-slate-200 rounded-full overflow-hidden flex">
                        {/* Parte ya asignada a otros */}
                        <div
                          className="h-full bg-blue-300"
                          style={{
                            width: `${Math.min(currentAllocatedPercent, 100)}%`,
                          }}
                          title={`Asignado a otros: ${formatCurrency(allocatedAmount)}`}
                        />
                        {/* Parte que se asignará a ESTE usuario (preview) */}
                        {salesTargetValue && (
                          <div
                            className={`h-full ${parseFloat(salesTargetValue) + allocatedAmount > totalPipelineGoal ? "bg-red-400" : "bg-green-500"} transition-all duration-500`}
                            style={{
                              width: `${Math.min((parseFloat(salesTargetValue) / totalPipelineGoal) * 100, 100 - currentAllocatedPercent)}%`,
                            }}
                            title={`Nueva asignación: ${formatCurrency(parseFloat(salesTargetValue))}`}
                          />
                        )}
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-slate-500">
                        <span>
                          Asignado:{" "}
                          {formatCurrency(
                            allocatedAmount +
                              (parseFloat(salesTargetValue) || 0),
                          )}
                        </span>
                        <span>
                          Disponible:{" "}
                          {formatCurrency(
                            Math.max(
                              0,
                              totalPipelineGoal -
                                allocatedAmount -
                                (parseFloat(salesTargetValue) || 0),
                            ),
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="p-6 border-t bg-slate-50">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {user ? "Actualizando..." : "Creando..."}
              </>
            ) : user ? (
              "Actualizar Usuario"
            ) : (
              "Crear Usuario"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

